/**
 * Menu Filtering Engine for RBAC
 * 
 * Filters sidebar menu based on user permissions.
 * Supports unlimited nesting levels.
 * 
 * Rules:
 * 1. If user doesn't have permission for a menu AND none of its children are permitted -> remove entire menu
 * 2. If user has permission for any child -> show parent menu
 * 3. Only render submenus user has permission for
 * 4. Remove all unauthorized submenu items
 * 5. Support unlimited nested levels
 * 6. Parent menu remains visible if at least one child is allowed
 * 7. Entire unauthorized menu branches don't render at all
 */

/**
 * Check if user has permission for a menu item
 * @param {object} menuItem - Menu item with optional permission field
 * @param {function} hasPermission - Permission check function
 * @param {boolean} isSuperAdmin - Is user super admin
 * @returns {boolean}
 */
const hasMenuPermission = (menuItem, hasPermission, isSuperAdmin) => {
  if (menuItem.superAdminOnly) return isSuperAdmin;

  // Super Admin has access to everything
  if (isSuperAdmin) return true;

  // No permission required
  if (!menuItem.permission) return true;

  // Check permission
  return hasPermission(menuItem.permission);
};

/**
 * Check if any child (recursively) has permission
 * @param {object} menuItem - Menu item with children
 * @param {function} hasPermission - Permission check function
 * @param {boolean} isSuperAdmin - Is user super admin
 * @returns {boolean}
 */
const hasAnyChildPermission = (menuItem, hasPermission, isSuperAdmin) => {
  if (!menuItem.children || menuItem.children.length === 0) {
    return false;
  }

  return menuItem.children.some((child) => {
    // Check if this child has permission
    if (hasMenuPermission(child, hasPermission, isSuperAdmin)) {
      return true;
    }

    // Recursively check if any of this child's children have permission
    return hasAnyChildPermission(child, hasPermission, isSuperAdmin);
  });
};

/**
 * Filter menu items recursively based on permissions
 * @param {array} menuItems - Array of menu items
 * @param {function} hasPermission - Permission check function from auth context
 * @param {boolean} isSuperAdmin - Is user super admin
 * @returns {array} Filtered menu items
 */
export const filterMenuByPermissions = (menuItems, hasPermission, isSuperAdmin) => {
  if (!menuItems || menuItems.length === 0) {
    return [];
  }

  return menuItems
    .map((item) => {
      // Create a copy to avoid mutating original
      const itemCopy = { ...item };

      // If item has children, filter them recursively
      if (itemCopy.children && itemCopy.children.length > 0) {
        itemCopy.children = filterMenuByPermissions(
          itemCopy.children,
          hasPermission,
          isSuperAdmin
        );
      }

      return itemCopy;
    })
    .filter((item) => {
      // Super Admin sees everything
      if (isSuperAdmin) return true;

      // Check if user has permission for this item
      const hasOwnPermission = hasMenuPermission(item, hasPermission, isSuperAdmin);

      // Check if user has permission for any children (recursively)
      const hasChildPermission = hasAnyChildPermission(item, hasPermission, isSuperAdmin);

      // Keep item if:
      // 1. User has permission for the item itself, OR
      // 2. User has permission for at least one child (at any nesting level)
      return hasOwnPermission || hasChildPermission;
    })
    .map((item) => {
      // Clean up: if parent has no children left after filtering, remove children property
      if (item.children && item.children.length === 0) {
        const { children, ...itemWithoutChildren } = item;
        return itemWithoutChildren;
      }
      return item;
    });
};

/**
 * Hook-friendly wrapper for menu filtering
 * Use this in components with useMemo for performance
 */
export const useFilteredMenu = (menuItems, hasPermission, isSuperAdmin) => {
  return filterMenuByPermissions(menuItems, hasPermission, isSuperAdmin);
};

export default filterMenuByPermissions;
